import React from 'react';
import { Calendar, Check, Clock, Image, Loader2, Palette, Plus, Save, School, Send, Trash2, Upload } from 'lucide-react';
import { InfoField } from '../SharedComponents';

const SettingsTab = ({ ctx }) => {
  const {  org, dashboard, members, calendarTerms, scheduleSlots, branding, alerts, schoolSettings, API_URL, getToken, fetchData, logoPreviewUrl, logoUploading, logoFile, showAddTerm, showAddSlot, termForm, slotForm, setTermForm, setSlotForm, setSchoolSettings, setBranding, setLogoPreviewUrl, setLogoUploading, setLogoFile, setShowAddTerm, setShowAddSlot, setOrg, logoFileRef , US_GRADES } = ctx;

  return (
<div data-testid="settings-tab">
  <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>School Profile</h1>
  <p className="text-xs mb-5" style={{ color: '#999' }}>
    Manage your organisation&apos;s branding. The logo you upload will appear in the team member dashboard header.
  </p>

  {/* Logo Upload Card */}
  <div className="bg-white rounded-xl p-6 mb-6" style={{ border: '1px solid #E8E8E8' }} data-testid="logo-upload-card">
    <h3 className="text-sm font-bold mb-4" style={{ color: '#1A1209' }}>School Logo</h3>

    {/* Current logo / upload area */}
    <div className="flex items-start gap-6 mb-5">
      <div className="w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden flex-shrink-0"
        style={{ borderColor: '#E0E0E0', backgroundColor: '#F8F7F4' }}>
        {(logoPreviewUrl || org?.logo_url) ? (
          <img
            src={logoPreviewUrl || `${API_URL}${org.logo_url}`}
            alt="School logo"
            className="max-w-full max-h-full object-contain"
            data-testid="current-logo-img"
          />
        ) : (
          <Image size={28} style={{ color: '#CCC' }} />
        )}
      </div>
      <div className="flex-1">
        <p className="text-xs mb-2" style={{ color: '#888' }}>
          Upload your organisation logo. Accepted formats: PNG, JPG, SVG. Maximum file size: 2MB.
        </p>
        <div className="flex gap-2">
          <input
            type="file"
            ref={logoFileRef}
            accept=".png,.jpg,.jpeg,.svg"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (file.size > 2 * 1024 * 1024) { alert('File exceeds 2MB limit'); return; }
              setLogoFile(file);
              setLogoPreviewUrl(URL.createObjectURL(file));
            }}
            data-testid="logo-file-input"
          />
          <button
            onClick={() => logoFileRef.current?.click()}
            className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5"
            style={{ backgroundColor: '#F0F0F0', color: '#555' }}
            data-testid="choose-logo-btn"
          >
            <Upload size={13} /> Choose File
          </button>
          {(logoPreviewUrl || org?.logo_url) && (
            <button
              onClick={async () => {
                if (!window.confirm('Remove the organisation logo?')) return;
                try {
                  await fetch(`${API_URL}/api/corporate/remove-logo`, {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${getToken()}` },
                  });
                  setLogoPreviewUrl(null);
                  setLogoFile(null);
                  setOrg(prev => ({ ...prev, logo_url: null }));
                } catch { alert('Failed to remove logo'); }
              }}
              className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5"
              style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }}
              data-testid="remove-logo-btn"
            >
              <Trash2 size={13} /> Remove
            </button>
          )}
        </div>
      </div>
    </div>

    {/* Live Preview Strip */}
    {(logoPreviewUrl || org?.logo_url) && (
      <div className="rounded-xl p-4 mb-5" style={{ backgroundColor: '#1A1209' }} data-testid="logo-preview-strip">
        <p className="text-[10px] font-medium mb-3 uppercase tracking-wider" style={{ color: '#A89880' }}>
          Preview — how it appears in the team member dashboard header
        </p>
        <div className="flex items-center gap-3" data-testid="logo-preview-content">
          <img
            src={logoPreviewUrl || `${API_URL}${org.logo_url}`}
            alt="School logo preview"
            style={{ height: 40, width: 'auto', maxWidth: 120, objectFit: 'contain' }}
            data-testid="preview-school-logo"
          />
          <div style={{ width: 1, height: 32, backgroundColor: '#555' }} />
          <img
            src="/hibff-logo.png"
            alt="HiBFF"
            style={{ height: 28, width: 'auto', objectFit: 'contain' }}
            data-testid="preview-hibff-logo"
          />
        </div>
      </div>
    )}

    {/* Save button (only when a new file is chosen) */}
    {logoFile && (
      <button
        onClick={async () => {
          setLogoUploading(true);
          try {
            const fd = new FormData();
            fd.append('file', logoFile);
            const res = await fetch(`${API_URL}/api/corporate/upload-logo`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${getToken()}` },
              body: fd,
            });
            const data = await res.json();
            if (data.success) {
              setOrg(prev => ({ ...prev, logo_url: data.logo_url }));
              setLogoFile(null);
              setLogoPreviewUrl(null);
            } else {
              alert(data.detail || 'Upload failed');
            }
          } catch { alert('Upload failed'); }
          setLogoUploading(false);
        }}
        disabled={logoUploading}
        className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white flex items-center gap-2 disabled:opacity-50"
        style={{ backgroundColor: '#2EAA5C' }}
        data-testid="save-logo-btn"
      >
        {logoUploading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
        {logoUploading ? 'Uploading...' : 'Save Logo'}
      </button>
    )}
  </div>

  {/* School Info (read-only) */}
  <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E8E8E8' }}>
    <h3 className="text-sm font-bold mb-4" style={{ color: '#1A1209' }}>School Information</h3>
    <div className="grid grid-cols-2 gap-4">
      <InfoField label="School Name" value={org?.org_name} />
      <InfoField label="Type" value={org?.org_type} />
      <InfoField label="District" value={org?.industry} />
      <InfoField label="State" value={org?.state} />
      <InfoField label="City" value={org?.city} />
      <InfoField label="ZIP" value={org?.zip_code} />
      <InfoField label="Contact" value={org?.contact_name} />
      <InfoField label="Email" value={org?.admin_email} />
    </div>
  </div>

  {/* Platform Preferences */}
  <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E8E8E8' }} data-testid="platform-prefs">
    <h3 className="text-sm font-bold mb-4" style={{ color: '#1A1209' }}>Platform Preferences</h3>

    {/* NFT Rewards Toggle */}
    <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#F3F4F6' }} data-testid="nft-toggle-row">
      <div>
        <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>NFT Rewards</p>
        <p className="text-[10px]" style={{ color: '#999' }}>
          {schoolSettings.nft_rewards_enabled
            ? 'Students earn blockchain-based NFT collectibles for achievements'
            : 'NFT rewards are disabled — students earn traditional badges and points only'}
        </p>
      </div>
      <button
        onClick={async () => {
          const newVal = !schoolSettings.nft_rewards_enabled;
          setSchoolSettings(p => ({ ...p, nft_rewards_enabled: newVal }));
          await fetch(`${API_URL}/api/corporate/settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify({ nft_rewards_enabled: newVal }),
          });
        }}
        className="w-12 h-6 rounded-full transition-colors relative flex-shrink-0"
        style={{ backgroundColor: schoolSettings.nft_rewards_enabled ? '#F5831F' : '#DDD' }}
        data-testid="nft-toggle"
      >
        <div className="w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all shadow-sm"
          style={{ left: schoolSettings.nft_rewards_enabled ? 26 : 2 }} />
      </button>
    </div>

    {/* Nudge Toggle */}
    <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#F3F4F6' }} data-testid="nudge-toggle-row">
      <div>
        <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>Engagement Nudges</p>
        <p className="text-[10px]" style={{ color: '#999' }}>Send automated reminders to inactive students</p>
      </div>
      <button
        onClick={async () => {
          const newVal = !schoolSettings.nudge_enabled;
          setSchoolSettings(p => ({ ...p, nudge_enabled: newVal }));
          await fetch(`${API_URL}/api/corporate/settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify({ nudge_enabled: newVal }),
          });
        }}
        className="w-12 h-6 rounded-full transition-colors relative flex-shrink-0"
        style={{ backgroundColor: schoolSettings.nudge_enabled ? '#2563EB' : '#DDD' }}
        data-testid="nudge-toggle"
      >
        <div className="w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all shadow-sm"
          style={{ left: schoolSettings.nudge_enabled ? 26 : 2 }} />
      </button>
    </div>

    {/* Nudge Threshold */}
    <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#F3F4F6' }}>
      <div>
        <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>Inactive Threshold</p>
        <p className="text-[10px]" style={{ color: '#999' }}>Days of inactivity before sending a nudge</p>
      </div>
      <select
        value={schoolSettings.nudge_inactive_days}
        onChange={async (e) => {
          const val = parseInt(e.target.value);
          setSchoolSettings(p => ({ ...p, nudge_inactive_days: val }));
          await fetch(`${API_URL}/api/corporate/settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify({ nudge_inactive_days: val }),
          });
        }}
        className="px-3 py-1.5 rounded-lg text-sm border"
        style={{ borderColor: '#E8E8E8' }}
        data-testid="nudge-days-select"
      >
        {[1, 2, 3, 5, 7, 14].map(d => <option key={d} value={d}>{d} day{d > 1 ? 's' : ''}</option>)}
      </select>
    </div>

    {/* Max Daily Usage */}
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>Daily Usage Limit</p>
        <p className="text-[10px]" style={{ color: '#999' }}>Recommended max minutes per day per student</p>
      </div>
      <select
        value={schoolSettings.max_daily_usage_minutes}
        onChange={async (e) => {
          const val = parseInt(e.target.value);
          setSchoolSettings(p => ({ ...p, max_daily_usage_minutes: val }));
          await fetch(`${API_URL}/api/corporate/settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify({ max_daily_usage_minutes: val }),
          });
        }}
        className="px-3 py-1.5 rounded-lg text-sm border"
        style={{ borderColor: '#E8E8E8' }}
        data-testid="daily-limit-select"
      >
        {[15, 30, 45, 60, 90, 120].map(m => <option key={m} value={m}>{m} min</option>)}
      </select>
    </div>
  </div>

  {/* School Branding */}
  <div className="bg-white rounded-xl p-6 mt-6" style={{ border: '1px solid #E8E8E8' }} data-testid="branding-section">
    <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}>
      <Palette size={14} style={{ color: '#8B5CF6' }} /> School Branding
    </h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="text-[10px] font-medium uppercase tracking-wider block mb-1" style={{ color: '#999' }}>Primary Color</label>
        <div className="flex items-center gap-2">
          <input type="color" value={branding.primary_color || '#F5831F'}
            onChange={e => setBranding(p => ({ ...p, primary_color: e.target.value }))}
            className="w-10 h-10 rounded-lg border-0 cursor-pointer" data-testid="brand-color-picker" />
          <span className="text-xs font-mono" style={{ color: '#666' }}>{branding.primary_color}</span>
        </div>
      </div>
      <div>
        <label className="text-[10px] font-medium uppercase tracking-wider block mb-1" style={{ color: '#999' }}>School Tagline</label>
        <input value={branding.tagline || ''} onChange={e => setBranding(p => ({ ...p, tagline: e.target.value }))}
          placeholder="e.g. Building Social Confidence Together"
          className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8E8E8' }} data-testid="brand-tagline" />
      </div>
    </div>
    <button onClick={async () => {
      await fetch(`${API_URL}/api/corporate/branding`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(branding),
      });
      alert('Branding updated!');
    }} className="mt-4 px-4 py-2 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: '#8B5CF6' }} data-testid="save-branding-btn">
      Save Branding
    </button>
  </div>

  {/* Academic Calendar */}
  <div className="bg-white rounded-xl p-6 mt-6" style={{ border: '1px solid #E8E8E8' }} data-testid="calendar-section">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
        <Calendar size={14} style={{ color: '#2563EB' }} /> Academic Calendar
      </h3>
      <button onClick={() => setShowAddTerm(!showAddTerm)}
        className="px-3 py-1.5 rounded-lg text-xs font-medium text-white flex items-center gap-1"
        style={{ backgroundColor: '#2563EB' }} data-testid="add-term-btn">
        <Plus size={11} /> Add Period
      </button>
    </div>
    <p className="text-[10px] mb-3" style={{ color: '#999' }}>Set term dates and holidays. Nudges and inactivity alerts will auto-pause during holiday periods.</p>

    {showAddTerm && (
      <div className="p-3 rounded-lg mb-3" style={{ backgroundColor: '#F8F7F4', border: '1px solid #E8E8E8' }}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          <input value={termForm.name} onChange={e => setTermForm(p => ({ ...p, name: e.target.value }))}
            placeholder="Period name *" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="term-name" />
          <input type="date" value={termForm.start_date} onChange={e => setTermForm(p => ({ ...p, start_date: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="term-start" />
          <input type="date" value={termForm.end_date} onChange={e => setTermForm(p => ({ ...p, end_date: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="term-end" />
          <select value={termForm.type} onChange={e => setTermForm(p => ({ ...p, type: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="term-type">
            <option value="term">Term</option>
            <option value="holiday">Holiday</option>
            <option value="exam">Exam Period</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={async () => {
            if (!termForm.name || !termForm.start_date || !termForm.end_date) return;
            await fetch(`${API_URL}/api/corporate/calendar/terms`, {
              method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
              body: JSON.stringify(termForm),
            });
            setTermForm({ name: '', start_date: '', end_date: '', type: 'term' });
            setShowAddTerm(false);
            fetchData();
          }} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: '#2EAA5C' }}>Save</button>
          <button onClick={() => setShowAddTerm(false)} className="px-3 py-1.5 rounded-lg text-xs" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>Cancel</button>
        </div>
      </div>
    )}

    {calendarTerms.length > 0 ? (
      <div className="space-y-2">
        {calendarTerms.map(t => {
          const typeColors = { term: '#2563EB', holiday: '#F59E0B', exam: '#DC2626' };
          const tc = typeColors[t.type] || '#999';
          return (
            <div key={t.term_id} className="flex items-center justify-between px-4 py-3 rounded-lg" style={{ backgroundColor: `${tc}08`, border: `1px solid ${tc}25` }} data-testid={`term-${t.term_id}`}>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tc }} />
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>{t.name}</p>
                  <p className="text-[10px]" style={{ color: '#999' }}>
                    {new Date(t.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {new Date(t.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded text-[9px] font-bold capitalize" style={{ backgroundColor: `${tc}15`, color: tc }}>{t.type}</span>
                <button onClick={async () => {
                  await fetch(`${API_URL}/api/corporate/calendar/terms/${t.term_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
                  fetchData();
                }} className="p-1 rounded hover:bg-gray-100"><Trash2 size={12} style={{ color: '#DC2626' }} /></button>
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <p className="text-xs text-center py-4" style={{ color: '#CCC' }}>No calendar periods set. Add your organisation terms and holidays.</p>
    )}
  </div>

  {/* Session Scheduling */}
  <div className="bg-white rounded-xl p-6 mt-6" style={{ border: '1px solid #E8E8E8' }} data-testid="schedule-section">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
        <Clock size={14} style={{ color: '#10B981' }} /> Session Schedule
      </h3>
      <button onClick={() => setShowAddSlot(!showAddSlot)}
        className="px-3 py-1.5 rounded-lg text-xs font-medium text-white flex items-center gap-1"
        style={{ backgroundColor: '#10B981' }} data-testid="add-slot-btn">
        <Plus size={11} /> Add Slot
      </button>
    </div>
    <p className="text-[10px] mb-3" style={{ color: '#999' }}>Define weekly practice windows for members. These appear in the student dashboard as scheduled sessions.</p>

    {showAddSlot && (
      <div className="p-3 rounded-lg mb-3" style={{ backgroundColor: '#F8F7F4', border: '1px solid #E8E8E8' }}>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2">
          <select value={slotForm.day} onChange={e => setSlotForm(p => ({ ...p, day: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="slot-day">
            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <input type="time" value={slotForm.start_time} onChange={e => setSlotForm(p => ({ ...p, start_time: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="slot-start" />
          <input type="time" value={slotForm.end_time} onChange={e => setSlotForm(p => ({ ...p, end_time: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="slot-end" />
          <select value={slotForm.grade} onChange={e => setSlotForm(p => ({ ...p, grade: e.target.value }))}
            className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="slot-grade">
            <option value="">All Teams</option>
            {US_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <input value={slotForm.label} onChange={e => setSlotForm(p => ({ ...p, label: e.target.value }))}
            placeholder="Label" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="slot-label" />
        </div>
        <div className="flex gap-2">
          <button onClick={async () => {
            await fetch(`${API_URL}/api/corporate/schedule`, {
              method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
              body: JSON.stringify(slotForm),
            });
            setSlotForm({ day: 'Monday', start_time: '09:00', end_time: '09:30', grade: '', label: 'Practice Session' });
            setShowAddSlot(false);
            fetchData();
          }} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: '#2EAA5C' }}>Save</button>
          <button onClick={() => setShowAddSlot(false)} className="px-3 py-1.5 rounded-lg text-xs" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>Cancel</button>
        </div>
      </div>
    )}

    {scheduleSlots.length > 0 ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {scheduleSlots.map(s => (
          <div key={s.slot_id} className="flex items-center justify-between px-3 py-2.5 rounded-lg" style={{ backgroundColor: '#F0FDF4', border: '1px solid #D1FAE5' }} data-testid={`slot-${s.slot_id}`}>
            <div>
              <p className="text-xs font-bold" style={{ color: '#065F46' }}>{s.day}</p>
              <p className="text-[10px]" style={{ color: '#059669' }}>{s.start_time} — {s.end_time}</p>
              <p className="text-[10px]" style={{ color: '#999' }}>{s.label} {s.grade ? `(${s.grade})` : '(All)'}</p>
            </div>
            <button onClick={async () => {
              await fetch(`${API_URL}/api/corporate/schedule/${s.slot_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
              fetchData();
            }} className="p-1 rounded hover:bg-gray-100"><Trash2 size={12} style={{ color: '#DC2626' }} /></button>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-xs text-center py-4" style={{ color: '#CCC' }}>No schedule slots. Add weekly practice sessions for your members.</p>
    )}
  </div>
</div>
  );
};

export default SettingsTab;
